import { ItemSales, EventDay } from "./types";
import { usd } from "./helpers";

interface SalesByDayTableProps {
  items: ItemSales[];
  eventDays: EventDay[];
  dayTotals: Record<string, { credit: number; rfid: number; cashBalance: number; tips: number }>;
  tips: number;
}

export const SalesByDayTable = ({ items, eventDays, dayTotals, tips }: SalesByDayTableProps) => {
  // Only show days that have any sales data
  const activeDays = eventDays.filter((day) => {
    const dt = dayTotals[day.date];
    if (dt && (dt.credit > 0 || dt.rfid > 0 || dt.cashBalance > 0 || dt.tips > 0)) return true;
    return items.some((item) => {
      const d = item.byDay[day.date];
      return d && (d.credit > 0 || d.rfid > 0 || d.cashBalance > 0);
    });
  });

  if (items.length === 0 && activeDays.length === 0) {
    return <p className="text-sm text-gray-400 py-3">No sales data available.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        {/* Day header row */}
        <thead>
          <tr className="bg-red-800 text-white">
            <th className="text-left py-1.5 px-2 font-semibold sticky left-0 bg-red-800 z-10 min-w-[140px]">
              Item
            </th>
            {activeDays.map((day) => (
              <th
                key={day.date}
                colSpan={3}
                className="text-center py-1.5 px-1 font-semibold border-l border-red-600"
              >
                {day.shortLabel}
              </th>
            ))}
            <th colSpan={3} className="text-center py-1.5 px-1 font-semibold border-l border-red-600">
              Total
            </th>
          </tr>
          {/* Sub-header row */}
          <tr className="bg-red-700 text-white text-[10px]">
            <th className="sticky left-0 bg-red-700 z-10" />
            {activeDays.map((day) => (
              <SubHeaders key={day.date} />
            ))}
            <SubHeaders />
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr
              key={item.itemName}
              className={idx % 2 === 0 ? "bg-gray-50" : "bg-white"}
            >
              <td className="py-1 px-2 font-medium text-gray-800 sticky left-0 z-10 bg-inherit truncate max-w-[180px]">
                {item.itemName}
              </td>
              {activeDays.map((day) => {
                const d = item.byDay[day.date];
                return <DayCells key={day.date} credit={d?.credit ?? 0} rfid={d?.rfid ?? 0} cashBalance={d?.cashBalance ?? 0} />;
              })}
              <DayCells credit={item.totalCredit} rfid={item.totalRfid} cashBalance={item.totalCashBalance} bold />
            </tr>
          ))}

          {/* Totals row */}
          <tr className="bg-gray-200 font-semibold border-t-2 border-gray-400">
            <td className="py-1.5 px-2 sticky left-0 bg-gray-200 z-10 text-gray-900">
              Totals
            </td>
            {activeDays.map((day) => {
              const dt = dayTotals[day.date];
              return <DayCells key={day.date} credit={dt?.credit ?? 0} rfid={dt?.rfid ?? 0} cashBalance={dt?.cashBalance ?? 0} bold />;
            })}
            <DayCells
              credit={Object.values(dayTotals).reduce((s, d) => s + d.credit, 0)}
              rfid={Object.values(dayTotals).reduce((s, d) => s + d.rfid, 0)}
              cashBalance={Object.values(dayTotals).reduce((s, d) => s + d.cashBalance, 0)}
              bold
            />
          </tr>

          {/* Tips row */}
          <tr className="border-t border-gray-300">
            <td className="py-1 px-2 font-medium text-gray-700 sticky left-0 bg-white z-10">
              Tips
            </td>
            {activeDays.map((day) => {
              const dt = dayTotals[day.date];
              return (
                <td key={day.date} colSpan={3} className="text-right py-1 px-2 text-gray-700 border-l border-gray-200">
                  {dt?.tips ? usd(dt.tips) : "-"}
                </td>
              );
            })}
            <td colSpan={3} className="text-right py-1 px-2 font-semibold text-gray-900 border-l border-gray-200">
              {usd(tips)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const SubHeaders = () => (
  <>
    <th className="text-right py-1 px-1.5 font-medium border-l border-red-500/40">Credit</th>
    <th className="text-right py-1 px-1.5 font-medium">RFID</th>
    <th className="text-right py-1 px-1.5 font-medium">Cash Bal</th>
  </>
);

const DayCells = ({
  credit,
  rfid,
  cashBalance,
  bold,
}: {
  credit: number;
  rfid: number;
  cashBalance: number;
  bold?: boolean;
}) => {
  const cls = `text-right py-1 px-1.5 tabular-nums ${bold ? "font-semibold text-gray-900" : "text-gray-700"}`;
  return (
    <>
      <td className={`${cls} border-l border-gray-200`}>{credit ? usd(credit) : "-"}</td>
      <td className={cls}>{rfid ? usd(rfid) : "-"}</td>
      <td className={cls}>{cashBalance ? usd(cashBalance) : "-"}</td>
    </>
  );
};
